
import { useState, useMemo } from 'react';
import BackendLayout from '../../components/feature/BackendLayout';
import { outreachStats, prospects, conversations } from '../../mocks/outreach';

type StatusFilter = 'all' | 'new' | 'contacted' | 'replied' | 'negotiating' | 'partner';

export default function OutreachPage() {
  const [selectedProspectId, setSelectedProspectId] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [composeSubject, setComposeSubject] = useState('');
  const [composeMessage, setComposeMessage] = useState('');
  const [prospectStatus, setProspectStatus] = useState<Record<string, string>>({});
  const [sendSuccess, setSendSuccess] = useState(false);

  const selectedProspect = useMemo(() =>
    prospects.find(p => p.id === selectedProspectId),
    [selectedProspectId]
  );

  const prospectConversations = useMemo(() =>
    conversations.filter(c => c.prospectId === selectedProspectId).sort((a, b) =>
      new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
    ),
    [selectedProspectId]
  );

  const filteredProspects = useMemo(() => {
    return prospects.filter(p => {
      const matchesStatus = statusFilter === 'all' || p.status === statusFilter;
      const matchesSearch = searchQuery === '' ||
        p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.company.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.email.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesStatus && matchesSearch;
    });
  }, [statusFilter, searchQuery]);

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      new: 'bg-sky-100 text-sky-700',
      contacted: 'bg-amber-100 text-amber-700',
      replied: 'bg-teal-100 text-teal-700',
      negotiating: 'bg-orange-100 text-orange-700',
      partner: 'bg-emerald-100 text-emerald-700'
    };
    return colors[status] || colors.new;
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const handleSendMessage = () => {
    if (!composeSubject.trim() || !composeMessage.trim()) return;
    setSendSuccess(true);
    setComposeSubject('');
    setComposeMessage('');
    setTimeout(() => setSendSuccess(false), 3000);
  };

  const handleStatusChange = (prospectId: string, newStatus: string) => {
    setProspectStatus(prev => ({ ...prev, [prospectId]: newStatus }));
  };

  const getCurrentStatus = (prospectId: string, defaultStatus: string) => {
    return prospectStatus[prospectId] || defaultStatus;
  };

  return (
    <BackendLayout>
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-1">Outreach</h1>
          <p className="text-sm text-gray-500">Reach prospects and manage conversations in one place</p>
        </div>
        <button className="px-5 py-2.5 bg-[#0A0A0A] text-white text-sm font-semibold rounded-lg hover:bg-gray-800 transition-colors flex items-center gap-2 whitespace-nowrap cursor-pointer">
          <i className="ri-add-line"></i> New Prospect
        </button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-5 mb-6">
        <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-teal-50 rounded-lg flex items-center justify-center flex-shrink-0">
              <i className="ri-group-line text-xl text-teal-600"></i>
            </div>
            <div>
              <div className="text-xl font-bold text-gray-900">{outreachStats.totalProspects}</div>
              <div className="text-xs text-gray-500">Total Prospects</div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-50 rounded-lg flex items-center justify-center flex-shrink-0">
              <i className="ri-reply-line text-xl text-emerald-600"></i>
            </div>
            <div>
              <div className="text-xl font-bold text-gray-900">{outreachStats.responseRate}%</div>
              <div className="text-xs text-gray-500">Response Rate</div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-sky-50 rounded-lg flex items-center justify-center flex-shrink-0">
              <i className="ri-chat-3-line text-xl text-sky-600"></i>
            </div>
            <div>
              <div className="text-xl font-bold text-gray-900">{outreachStats.activeConversations}</div>
              <div className="text-xs text-gray-500">Active Conversations</div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-50 rounded-lg flex items-center justify-center flex-shrink-0">
              <i className="ri-handshake-line text-xl text-amber-600"></i>
            </div>
            <div>
              <div className="text-xl font-bold text-gray-900">{outreachStats.dealsClosed}</div>
              <div className="text-xs text-gray-500">Deals Closed</div>
            </div>
          </div>
        </div>
      </div>

      {/* Three Column Layout */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="grid grid-cols-1 lg:grid-cols-12" style={{ minHeight: '560px' }}>

          {/* Left Sidebar - Prospects List */}
          <div className="lg:col-span-3 border-r border-gray-100">
            <div className="p-3 border-b border-gray-100">
              <div className="relative">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
                <input
                  type="text"
                  placeholder="Search prospects..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 text-gray-900 placeholder-gray-400 bg-white"
                />
              </div>
            </div>

            <div className="p-2.5 border-b border-gray-100">
              <div className="flex flex-wrap gap-1">
                {(['all', 'new', 'contacted', 'replied', 'negotiating', 'partner'] as StatusFilter[]).map(status => (
                  <button
                    key={status}
                    onClick={() => setStatusFilter(status)}
                    className={`px-2.5 py-1 text-xs rounded-full font-medium whitespace-nowrap transition-colors cursor-pointer ${
                      statusFilter === status
                        ? 'bg-[#0A0A0A] text-white'
                        : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                    }`}
                  >
                    {status.charAt(0).toUpperCase() + status.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            <div className="overflow-y-auto" style={{ maxHeight: '440px' }}>
              {filteredProspects.map(prospect => {
                const lastMessage = conversations
                  .filter(c => c.prospectId === prospect.id)
                  .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())[0];

                return (
                  <div
                    key={prospect.id}
                    onClick={() => setSelectedProspectId(prospect.id)}
                    className={`p-3.5 border-b border-gray-50 cursor-pointer transition-colors ${
                      selectedProspectId === prospect.id
                        ? 'bg-gray-50 border-l-2 border-l-[#0A0A0A]'
                        : 'hover:bg-gray-50/50'
                    }`}
                  >
                    <div className="flex items-start gap-2.5">
                      <img
                        src={prospect.avatar}
                        alt={prospect.name}
                        className="w-9 h-9 rounded-full object-cover flex-shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2 mb-0.5">
                          <h3 className="text-sm font-semibold text-gray-900 truncate">{prospect.name}</h3>
                          <span className={`px-2 py-0.5 text-[10px] rounded-full font-medium whitespace-nowrap ${getStatusColor(prospect.status)}`}>
                            {prospect.status}
                          </span>
                        </div>
                        <div className="text-xs text-gray-400 mb-1 truncate">{prospect.company}</div>
                        {lastMessage && (
                          <>
                            <div className="text-xs text-gray-500 truncate mb-0.5">
                              {lastMessage.direction === 'sent' ? 'You: ' : ''}{lastMessage.subject}
                            </div>
                            <div className="text-[10px] text-gray-400">
                              {formatTimestamp(lastMessage.timestamp)}
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Middle Panel - Conversation Thread */}
          <div className="lg:col-span-6 flex flex-col border-r border-gray-100">
            {selectedProspect ? (
              <>
                <div className="p-4 border-b border-gray-100">
                  <div className="flex items-center gap-3">
                    <img src={selectedProspect.avatar} alt={selectedProspect.name} className="w-10 h-10 rounded-full object-cover" />
                    <div>
                      <h2 className="text-sm font-bold text-gray-900">{selectedProspect.name}</h2>
                      <div className="text-xs text-gray-500">{selectedProspect.company} &middot; {selectedProspect.role}</div>
                    </div>
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto p-4 space-y-3" style={{ maxHeight: '320px' }}>
                  {prospectConversations.length > 0 ? (
                    prospectConversations.map(msg => (
                      <div key={msg.id} className={`${msg.direction === 'sent' ? 'ml-auto' : 'mr-auto'} max-w-[85%]`}>
                        <div className={`p-3.5 rounded-xl ${
                          msg.direction === 'sent'
                            ? 'bg-gray-900 text-white'
                            : 'bg-gray-50 border border-gray-100'
                        }`}>
                          <div className="flex items-center justify-between mb-1.5">
                            <div className={`text-xs font-semibold ${msg.direction === 'sent' ? 'text-white/70' : 'text-gray-600'}`}>
                              {msg.direction === 'sent' ? 'You' : selectedProspect.name}
                            </div>
                            <div className={`text-[10px] ${msg.direction === 'sent' ? 'text-white/40' : 'text-gray-400'}`}>
                              {new Date(msg.timestamp).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                            </div>
                          </div>
                          <div className={`text-xs font-semibold mb-1 ${msg.direction === 'sent' ? 'text-white/90' : 'text-gray-900'}`}>
                            {msg.subject}
                          </div>
                          <div className={`text-xs whitespace-pre-wrap leading-relaxed ${msg.direction === 'sent' ? 'text-white/70' : 'text-gray-600'}`}>
                            {msg.body}
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center text-gray-400 py-12">
                      <i className="ri-chat-3-line text-3xl mb-2 block"></i>
                      <p className="text-sm">No messages yet. Start the conversation below.</p>
                    </div>
                  )}
                </div>

                <div className="p-4 border-t border-gray-100 bg-gray-50/50">
                  {sendSuccess && (
                    <div className="mb-2.5 px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-lg text-xs text-emerald-700 flex items-center gap-2">
                      <i className="ri-check-line"></i> Message sent successfully!
                    </div>
                  )}
                  <div className="space-y-2.5">
                    <input
                      type="text"
                      placeholder="Subject"
                      value={composeSubject}
                      onChange={(e) => setComposeSubject(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3.5 py-2 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900 bg-white"
                    />
                    <textarea
                      placeholder="Write your message..."
                      value={composeMessage}
                      onChange={(e) => { if (e.target.value.length <= 500) setComposeMessage(e.target.value); }}
                      maxLength={500}
                      rows={3}
                      className="w-full border border-gray-300 rounded-lg px-3.5 py-2 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900 resize-none bg-white"
                    />
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] text-gray-400">{composeMessage.length}/500</span>
                      <button
                        onClick={handleSendMessage}
                        disabled={!composeSubject.trim() || !composeMessage.trim()}
                        className="px-5 py-2 bg-[#0A0A0A] text-white font-semibold text-sm rounded-lg whitespace-nowrap hover:bg-gray-800 transition-colors disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
                      >
                        <i className="ri-send-plane-fill mr-1.5"></i>Send
                      </button>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <i className="ri-mail-line text-2xl text-gray-400"></i>
                  </div>
                  <h3 className="text-base font-bold text-gray-700 mb-1">Select a Prospect</h3>
                  <p className="text-xs text-gray-400">Choose a prospect from the list to view conversation history</p>
                </div>
              </div>
            )}
          </div>

          {/* Right Sidebar - Prospect Details */}
          <div className="lg:col-span-3">
            {selectedProspect ? (
              <div className="p-4">
                <h3 className="text-sm font-bold text-gray-900 mb-4">Prospect Details</h3>
                <div className="space-y-4">
                  <div>
                    <div className="text-[10px] text-gray-400 mb-1 font-medium uppercase tracking-wider">Status</div>
                    <select
                      value={getCurrentStatus(selectedProspect.id, selectedProspect.status)}
                      onChange={(e) => handleStatusChange(selectedProspect.id, e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-900 cursor-pointer bg-white"
                    >
                      <option value="new">New</option>
                      <option value="contacted">Contacted</option>
                      <option value="replied">Replied</option>
                      <option value="negotiating">Negotiating</option>
                      <option value="partner">Partner</option>
                    </select>
                  </div>
                  <div>
                    <div className="text-[10px] text-gray-400 mb-1 font-medium uppercase tracking-wider">Email</div>
                    <a href={`mailto:${selectedProspect.email}`} className="text-sm text-teal-600 hover:underline break-all">{selectedProspect.email}</a>
                  </div>
                  <div>
                    <div className="text-[10px] text-gray-400 mb-1 font-medium uppercase tracking-wider">Company</div>
                    <div className="text-sm text-gray-900">{selectedProspect.company}</div>
                  </div>
                  <div>
                    <div className="text-[10px] text-gray-400 mb-1 font-medium uppercase tracking-wider">Role</div>
                    <div className="text-sm text-gray-900">{selectedProspect.role}</div>
                  </div>
                  <div>
                    <div className="text-[10px] text-gray-400 mb-1 font-medium uppercase tracking-wider">Website</div>
                    <a href={selectedProspect.website} target="_blank" rel="noopener noreferrer nofollow" className="text-sm text-teal-600 hover:underline break-all">{selectedProspect.website}</a>
                  </div>
                  <div>
                    <div className="text-[10px] text-gray-400 mb-1 font-medium uppercase tracking-wider">Last Contacted</div>
                    <div className="text-sm text-gray-900">
                      {new Date(selectedProspect.lastContacted).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] text-gray-400 mb-1 font-medium uppercase tracking-wider">Notes</div>
                    <div className="text-xs text-gray-600 bg-gray-50 border border-gray-100 rounded-lg p-2.5 leading-relaxed">{selectedProspect.notes}</div>
                  </div>
                  <div className="pt-2 space-y-2">
                    <button className="w-full px-3 py-2 bg-[#0A0A0A] text-white font-semibold text-xs rounded-lg whitespace-nowrap hover:bg-gray-800 transition-colors cursor-pointer">
                      <i className="ri-phone-line mr-1.5"></i>Schedule Call
                    </button>
                    <button className="w-full px-3 py-2 bg-white border border-gray-200 text-gray-700 font-semibold text-xs rounded-lg whitespace-nowrap hover:bg-gray-50 transition-colors cursor-pointer">
                      <i className="ri-file-text-line mr-1.5"></i>View Contract
                    </button>
                    <button className="w-full px-3 py-2 bg-white border border-gray-200 text-gray-700 font-semibold text-xs rounded-lg whitespace-nowrap hover:bg-gray-50 transition-colors cursor-pointer">
                      <i className="ri-links-line mr-1.5"></i>Create Affiliate Link
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="p-5 flex items-center justify-center h-full">
                <div className="text-center text-gray-400 text-xs">Select a prospect to view details</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </BackendLayout>
  );
}
